import React, { useState, useEffect, useContext } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import BuyersTable from 'components/admin/NPH/BuyersTable'
import LeadsTable from 'components/admin/NPH/LeadsTable'
import PensionTotals from 'components/admin/NPH/PensionTotals'
import PensionTypePie from 'components/admin/NPH/PensionTypePie'
import PensionSizePie from 'components/admin/NPH/PensionSizePie'
import PensionTypeBar from 'components/admin/NPH/PensionTypeBar'
import PensionSizeBar from 'components/admin/NPH/PensionSizeBar'
import NPHTotalRevenue from 'components/admin/NPH/NPHTotalRevenue'
import PageAttribution from 'components/admin/NPH/PageAttribution'
import { getMoments } from 'config/helpers/Helpers'
import componentStyles from 'assets/theme/views/admin/alternative-dashboard.js'
import componentStylesCardDeck from 'assets/theme/components/cards/card-deck.js'
import moment from 'moment-timezone'
import PeriodSelect from 'components/general/PeriodSelect'

const useStyles = makeStyles(componentStyles)
const useStylesCardDeck = makeStyles(componentStylesCardDeck)

function NationalPensionHelpline() {
    const classes = { ...useStyles(), ...useStylesCardDeck() }
    const api = new AdminAPI()

    const [totals, setTotals] = useState(null)
    const [allBuyers, setAllBuyers] = useState([])
    const [buyers, setBuyers] = useState([])
    const [leads, setLeads] = useState([])
    const [showLeads, setShowLeads] = useState([])
    const [periodLeads, setPeriodLeads] = useState([])
    const [showLeadsStatus, setShowLeadsStatus] = useState('Unallocated')
    const [period, setPeriod] = useState('thisWeek')
    const [companies, setCompanies] = useState([])
    const [returns, setReturns] = useState([])
    const [periodReturns, setPeriodReturns] = useState([])
    const [payments, setPayments] = useState([])
    const [revenueBreakdown, setRevenueBreakdown] = useState(null)
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [loading, setLoading] = useState(true)
    const [alert, setAlert] = useState(false)

    useEffect(() => {
        getData()
    }, [])

    async function fetchLeadsAndBuyers(dates, buyers, returns) {
        const leads = await api.getNPHLeadsPeriod(dates.fromDate.valueOf(), dates.toDate.valueOf());
        console.log('all leads: ', leads);
        leads.sort((a, b) => b.createdAt - a.createdAt);

        let updatedBuyers = buyers.map((buyer) => ({ ...buyer, leadCount: 0, returnCount: 0 }));
        let totals = { all: 0, allocated: 0, rejected: 0, delivered: 0, fundsAll: 0 };
        let weekLeads = [];

        for (let lead of leads) {
            totals.all += 1;
            if (lead.source === 'funds.nationalpensionhelpline.ie') {
                totals.fundsAll += 1;
            }
            if (lead.companyId === null && !lead.isValid) {
                totals.rejected += 1;
            }
            if (lead.companyId) {
                totals.allocated += 1;
            }
            for (let buyer of updatedBuyers) {
                if (buyer.companyId === lead.companyId) {
                    if (buyer.companyId === 'aa' && buyer.type === 'Allocation' && lead.quote.type === 'Start Pension') {
                        buyer.leadCount += 1;
                    } else if (buyer.companyId === 'aa' && buyer.type === 'Balance' && lead.quote.type !== 'Start Pension') {
                        buyer.leadCount += 1;
                    } else if (buyer.companyId !== 'aa') {
                        buyer.leadCount += 1;
                    }
                }
            }
            weekLeads.push(lead);
        }

        let weekReturns = [];
        for (let request of returns) {
            let created = moment(request.leadCreatedAt);
            if (created.isBetween(dates.fromDate, dates.toDate)) {
                weekReturns.push(request);
                for (let buyer of updatedBuyers) {
                    if (buyer.companyId === request.companyId) {
                        buyer.returnCount += 1;
                    }
                }
            }
        }

        console.log('totals: ', totals);
        console.log('buyers: ', updatedBuyers);
        return { leads, updatedBuyers, totals, weekLeads, weekReturns };
    }

    function computeRevenueBreakdown(payments, aaStartPensionCount, fromDate, toDate) {
        // Topups: all topup payments except AL (pension compare), filtered to period
        const isTopup = p => p.billingMethod && p.billingMethod.toLowerCase() === 'topup'
        const topupTotal = payments
            .filter(p => isTopup(p))
            .filter(p => p.companyId !== 'al')
            .filter(p => moment(p.date).isBetween(fromDate, toDate, null, '[]'))
            .reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0)

        // Weekly deliveries: any payment with a pensions allocation line, excluding AL and AA, in period
        // Not filtered by active buyers — inactive companies that had invoices in the period are included
        let weeklyDeliveryTotal = 0
        let weeklyDeliveryInvoiceCount = 0
        let weeklyDeliveryLeadCount = 0

        for (const p of payments) {
            if (isTopup(p)) continue
            if (p.companyId === 'al') continue  // AL = Pension Compare, excluded
            if (p.companyId === 'aa') continue  // AA calculated from delivered leads below
            if (!moment(p.date).isBetween(fromDate, toDate, null, '[]')) continue
            const pensionLine = p.allocation && p.allocation['pensions']
            if (!pensionLine) continue
            const leads = parseFloat(pensionLine.allocation) || 0
            if (leads === 0) continue
            const leadPrice = parseFloat(pensionLine.leadPrice) || 0
            const subtotal = leads * leadPrice
            const vatRate = parseFloat(p.vatRate) || 0
            weeklyDeliveryTotal += subtotal * (1 + vatRate)
            weeklyDeliveryInvoiceCount += 1
            weeklyDeliveryLeadCount += leads
        }

        // Start Pensions (AA): all NPH start pension applications in period × €40 (invoiced outside system)
        const startPensionLeadCount = aaStartPensionCount
        const startPensionTotal = startPensionLeadCount * 40

        return { topupTotal, weeklyDeliveryTotal, weeklyDeliveryInvoiceCount, weeklyDeliveryLeadCount, startPensionTotal, startPensionLeadCount }
    }

    async function getData() {
        try {
            let dates = getMoments(period);

            const buyers = await api.getActiveNPHBuyers();
            console.log('buyers: ', buyers);

            const companies = await api.getCompanies();
            const activeCompanies = companies.filter((company) => company.active).sort((a, b) => a.name.localeCompare(b.name));
            console.log('companies: ', activeCompanies);

            const returns = await api.getReturnRequests();
            console.log('returns: ', returns);
            const pensionReturns = returns.filter((request) => request.product === 'pensions' && request.status === 'accepted');

            const allPayments = await api.getPayments();
            console.log('payments: ', allPayments);

            const spApps = await api.getStartPensionApplications('nph', dates.fromDate.valueOf(), dates.toDate.valueOf());
            console.log('nph sp apps: ', spApps.length);

            const { leads, updatedBuyers, totals, weekLeads, weekReturns } = await fetchLeadsAndBuyers(dates, buyers, pensionReturns);

            const breakdown = computeRevenueBreakdown(allPayments, spApps.length, dates.fromDate, dates.toDate);

            setAllBuyers(updatedBuyers);
            setBuyers(updatedBuyers.filter((buyer) => buyer.active));
            setLeads(leads);
            setShowLeads(leads.filter((lead) => lead.companyId === null && lead.isValid));
            setPeriodLeads(weekLeads);
            setCompanies(activeCompanies);
            setTotals(totals);
            setReturns(pensionReturns);
            setPeriodReturns(weekReturns);
            setPayments(allPayments);
            setRevenueBreakdown(breakdown);
            setFromDate(dates.fromDate);
            setToDate(dates.toDate);
            setLoading(false);
        } catch (e) {
            onError(e);
        }
    }

    async function updatePeriod(period, fromDate = null, toDate = null) {
        setPeriod(period);
        setLoading(true);

        let dates = period === 'custom' ? {
            fromDate: moment(fromDate).startOf('day'),
            toDate: moment(toDate).endOf('day')
        } : getMoments(period);

        try {
            const { leads, updatedBuyers, totals, weekLeads, weekReturns } = await fetchLeadsAndBuyers(dates, buyers, returns);

            const spApps = await api.getStartPensionApplications('nph', dates.fromDate.valueOf(), dates.toDate.valueOf());
            console.log('nph sp apps: ', spApps.length);

            const breakdown = computeRevenueBreakdown(payments, spApps.length, dates.fromDate, dates.toDate);

            setPeriodLeads(weekLeads);
            setTotals(totals);
            setPeriodReturns(weekReturns);
            setRevenueBreakdown(breakdown);
            setFromDate(dates.fromDate);
            setToDate(dates.toDate);
            setLoading(false);
        } catch (e) {
            onError(e);
        }
    }

    function updateShowLeadsStatus(showLeadsStatus) {
        // if companyId === null lead is unallocated, if companyId does not exist it is from Start Pension
        let showLeads = []
        if (showLeadsStatus === 'Unallocated') {
            showLeads = leads.filter((lead) => lead.companyId === null && lead.isValid)
        }
        else if (showLeadsStatus === 'Allocated') {
            showLeads = leads.filter((lead) => lead.companyId !== null)
        }
        else if (showLeadsStatus === 'Rejected') {
            //showLeads = leads.filter((lead) => lead.companyId === null && !lead.isValid)
            showLeads = leads.filter((lead) => !lead.companyId && !lead.isValid)
        }
        setShowLeads(showLeads)
        setShowLeadsStatus(showLeadsStatus)
    }

    function updateLead(lead) {
        const index = leads.findIndex(oldLead => (oldLead.leadId === lead.leadId))
        console.log('lead index: ', index)

        let newLeads = [...leads]
        newLeads[index] = lead
        console.log(newLeads)
        //setLeads(newLeads.filter((lead) => lead.companyId === null))
        let showLeads = []
        if (showLeadsStatus === 'Unallocated') {
            showLeads = newLeads.filter((lead) => lead.companyId === null && lead.isValid)
        }
        else if (showLeadsStatus === 'Allocated') {
            showLeads = newLeads.filter((lead) => lead.companyId !== null)
        }
        else if (showLeadsStatus === 'Rejected') {
            showLeads = newLeads.filter((lead) => lead.companyId === null && !lead.isValid)
        }
        console.log('show leads: ', showLeads)
        setShowLeads(showLeads)
        setLeads(newLeads)
    }

    function showSuccessAlert(text) {
        setAlert(
            <ReactBSAlert
                success
                style={{ display: 'block', marginTop: '-100px' }}
                title='Success'
                onConfirm={() => setAlert(false)}
                onCancel={() => setAlert(false)}
                confirmBtnBsStyle='success'
                confirmBtnText='Ok'
                btnSize=''
                confirmBtnStyle={{
                    marginRight: undefined,
                    borderColor: undefined,
                    boxShadow: undefined,
                }}
            >
                {text}
            </ReactBSAlert>
        )
    }

    return (
        <>
            {alert}
            <Container
                maxWidth={false}
                component={Box}
                //marginTop='-6rem'
                marginTop='2rem'
                classes={{ root: classes.containerRoot }}
            >
                {!loading ? (
                    <>
                        <Grid container>
                            <Grid item xs={12}>
                                <Grid
                                    container
                                    component={Box}
                                    alignItems='center'
                                    justifyContent='flex-start'
                                    style={{marginBottom: '2rem'}}
                                    direction='row'
                                >
                                    <Grid item xs={12} md={2}>
                                        <PeriodSelect
                                            period={period}
                                            updatePeriod={(period, fromDate, toDate) => updatePeriod(period, fromDate, toDate)}
                                            fromDate={fromDate}
                                            toDate={toDate}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <PensionTotals 
                                    totals={totals}
                                    // all non rejected leads
                                    leads={periodLeads.filter((lead) => lead.companyId || lead.isValid)}
                                    returns={periodReturns}
                                    buyers={buyers}
                                    allBuyers={allBuyers}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <NPHTotalRevenue
                                    revenueBreakdown={revenueBreakdown}
                                    period={period}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <PageAttribution
                                    leads={periodLeads.filter((lead) => lead.companyId || lead.isValid)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <BuyersTable 
                                    buyers={buyers}
                                    total={totals.all}
                                    direction='column'
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <PensionTypePie
                                    // all leads
                                    leads={periodLeads}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <PensionSizePie
                                    // all leads
                                    leads={periodLeads}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <PensionTypeBar
                                    // all non rejected leads
                                    leads={periodLeads.filter((lead) => lead.companyId || lead.isValid)} 
                                    returns={periodReturns}
                                    buyers={buyers}
                                    allBuyers={allBuyers}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <PensionSizeBar
                                    // all non rejected leads
                                    leads={periodLeads.filter((lead) => lead.companyId || lead.isValid)} 
                                    returns={periodReturns}
                                    buyers={buyers}
                                    allBuyers={allBuyers}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Grid
                                    container
                                    component={Box}
                                    alignItems='center'
                                    justifyContent='flex-start'
                                    style={{marginBottom: '2rem'}}
                                    direction='row'
                                >
                                    <Grid item xs={12} md={2}>
                                        <FormGroup style={{marginBottom: '0'}}>
                                            <FormControl variant='outlined' fullWidth>
                                                <Select
                                                    multiple={false}
                                                    value={showLeadsStatus}
                                                    onChange={(e) => updateShowLeadsStatus(e.target.value)}
                                                >
                                                    <MenuItem value={'Unallocated'}>Unallocated</MenuItem>
                                                    <MenuItem value={'Allocated'}>Allocated</MenuItem>
                                                    <MenuItem value={'Rejected'}>Rejected</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <LeadsTable
                                    leads={showLeads}
                                    title={showLeadsStatus}
                                    refreshLeads={() => getData()}
                                    updateLead={(lead) => updateLead(lead)}
                                    showSuccessAlert={(text) => showSuccessAlert(text)}
                                    companies={companies}
                                />
                            </Grid>
                        </Grid>
                        <Grid container style={{marginTop: '2rem'}}>
                            <Grid item xs={12}>
                                
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                                <Loader />
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    );
}

export default NationalPensionHelpline